/*eslint @typescript-eslint/no-explicit-any: ["off"]*/

export interface Constructable {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
	constructor: Function;
}

export type ExtractReturnType<T extends (...args: any) => Promise<any>> = T extends (
	...args: any
) => infer R
	? Awaited<R>
	: never;

export async function promiseAllBatched<F extends ((...args: any) => Promise<any>)[]>(
	fns: F,
	batchSize: number
): Promise<{
	[P in keyof F]: ExtractReturnType<F[P]>;
}> {
	let i = 0;
	let results: any = [];

	while (i < fns.length) {
		const fnsToExecute = fns.slice(i, i + batchSize);
		const awaited = await Promise.all(fnsToExecute.map((f) => f()));
		results = [...results, ...Array.from(awaited)];
		i += batchSize;
	}

	return results;
}

export interface RetryOptions {
	maxTries?: number;
	pauseTime?: number;
	backoffPause?: boolean;
	retryCallback?: (tryNumber: number) => Promise<void>;
	shouldRetry?: (error: unknown) => boolean;
	jitterRatio?: number;
}

interface RetryOptionsForDecorator {
	maxTries?: number;
	pauseTime?: number;
	backoffPause?: boolean;
	retryCallback?: string;
}

export function Retry(
	options?: RetryOptionsForDecorator
): (
	target: Constructable,
	propertyKey: string,
	descriptor: TypedPropertyDescriptor<(...args: unknown[]) => Promise<unknown>>
) => void {
	return function (_target, _propertyKey, descriptor) {
		const origMethod = descriptor.value;

		if (!origMethod) {
			return;
		}

		descriptor.value = async function (...args: unknown[]): Promise<unknown> {
			const retryOptions: RetryOptions = {
				maxTries: options?.maxTries,
				pauseTime: options?.pauseTime,
				backoffPause: options?.backoffPause,
			};

			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore Type '(...args: unknown[]) => Promise<unknown>' is not assignable to type '(tryNumber: number) => Promise<void>'.
			if (options?.retryCallback && this[options.retryCallback]) {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore Type '(...args: unknown[]) => Promise<unknown>' is not assignable to type '(tryNumber: number) => Promise<void>'.
				retryOptions.retryCallback = this[options.retryCallback].bind(this);
			}

			return await retryFunction(async () => origMethod.apply(this, args), retryOptions);
		};
	};
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function retryFunction<F extends (...args: any) => any | Promise<any>>(
	fn: F,
	options?: RetryOptions
): Promise<ExtractReturnType<F>> {
	const maxTries = Math.max(options?.maxTries || 5, 1);
	let pauseTime = Math.max(options?.pauseTime || 1000, 0);
	const backoffPause = typeof options?.backoffPause === 'undefined' ? true : options.backoffPause;
	const jitterRatio = Math.min(Math.max(options?.jitterRatio ?? 0, 0), 1);

	let tries = 0;

	for (;;) {
		try {
			if (tries > 0) {
				await options?.retryCallback?.(tries);
			}

			return await fn();
		} catch (e) {
			tries += 1;

			if (tries >= maxTries || options?.shouldRetry?.(e) === false) {
				throw e;
			}

			await sleep(withJitter(pauseTime, jitterRatio));

			if (backoffPause) {
				// d.skuza: arbitrary value
				pauseTime *= 1.5;
			}
		}
	}
}

function withJitter(pauseTime: number, jitterRatio: number): number {
	if (jitterRatio === 0) {
		return pauseTime;
	}
	const spread = pauseTime * jitterRatio;
	return Math.max(0, Math.round(pauseTime - spread + Math.random() * spread * 2));
}

export async function sleep(time: number): Promise<void> {
	return new Promise((resolve) => {
		setTimeout(() => resolve(), time);
	});
}

export const DB_LOOKUP_TIMEOUT_MS = 5000;

export async function withTimeout<T>(
	promise: Promise<T>,
	errorOrMessage: Error | string,
	timeoutMs: number = DB_LOOKUP_TIMEOUT_MS
): Promise<T> {
	let timeoutId: ReturnType<typeof setTimeout>;
	const error = typeof errorOrMessage === 'string' ? new Error(errorOrMessage) : errorOrMessage;
	const timeoutPromise = new Promise<never>((_, reject) => {
		timeoutId = setTimeout(() => reject(error), timeoutMs);
	});

	try {
		return await Promise.race([promise, timeoutPromise]);
	} finally {
		clearTimeout(timeoutId!);
	}
}
